import type { AccountInfo } from '@azure/msal-browser';
import { msalInstance } from './msal';

/**
 * Login-hint persistence.
 *
 * Mirrors legacy `RaptorMsalService.loginRedirect`, which reads
 * `ey.user.context.idTokenClaims.preferred_username` and passes it as
 * `loginHint` so a returning user — or a fresh tab when MSAL caches in
 * sessionStorage (`enableSessionStorage`) — re-authenticates silently without
 * re-selecting their account.
 *
 * Stored in localStorage so it survives tab close and is shared across tabs.
 * The hint is a non-secret username, never a token.
 */

const LOGIN_HINT_KEY = 'EY_RAPToR_loginHint';

/** Persist the signed-in user's preferred_username (or username) as the hint. */
export function saveLoginHint(account: AccountInfo | null): void {
  if (!account) return;
  const claims = (account.idTokenClaims ?? {}) as { preferred_username?: string };
  const hint = claims.preferred_username || account.username;
  if (!hint) return;
  try {
    localStorage.setItem(LOGIN_HINT_KEY, hint);
  } catch {
    /* private mode — the hint is a UX nicety, safe to skip */
  }
}

/** The saved login hint, or undefined when none is stored. */
export function getLoginHint(): string | undefined {
  try {
    return localStorage.getItem(LOGIN_HINT_KEY) || undefined;
  } catch {
    return undefined;
  }
}

/** Read a login hint from a JSON user-context blob in sessionStorage. */
function readHintFromSessionContext(key: string): string | undefined {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as {
      username?: string;
      idTokenClaims?: { email?: string; preferred_username?: string };
    };
    return (
      parsed.idTokenClaims?.preferred_username ||
      parsed.idTokenClaims?.email ||
      parsed.username ||
      undefined
    );
  } catch {
    return undefined;
  }
}

/**
 * Best available login hint for Auth0 redirects (PRTL-2856): prefer the LIVE
 * session identity so the Auth0 account picker is pre-filled even before the
 * persisted MSAL hint exists. Order:
 *   1) session user context (React `ey.user.context` + legacy `userContext`),
 *   2) active MSAL account username,
 *   3) persisted `getLoginHint()`.
 */
export function getAuth0LoginHint(): string | undefined {
  const fromSession =
    readHintFromSessionContext('userContext') || readHintFromSessionContext('ey.user.context');
  if (fromSession) return fromSession;

  const activeUsername = msalInstance.getActiveAccount()?.username;
  if (activeUsername) return activeUsername;

  return getLoginHint();
}
